import React, { lazy, Suspense } from 'react';
import { useSelector } from 'react-redux';

/**
 * Loads the tour runner only once a tour is actually running.
 *
 * `JoyrideTourRunner` already returned `null` whenever there was no active
 * tour, but it was a static import from `Dashboard.js`, so react-joyride and
 * its dependency chain -- react-floater, plus a second, legacy copy of
 * popper.js v1 alongside the @popperjs/core v2 that MUI uses -- sat in the
 * initial bundle for every user on every page, 91 KB to render nothing almost
 * all of the time.
 *
 * This gate is the cheap part that stays eager: one field-level selector. The
 * expensive part is fetched on the first tour start and then cached like any
 * other chunk.
 */
const JoyrideTourRunner = lazy(() => import('./JoyrideTourRunner'));

const JoyrideTourGate = () => {
  // Deliberately not `isRunning`: the runner owns the whole tour lifecycle,
  // including pausing between steps, and unmounting it mid-tour would reset
  // that state. `activeTourId` is set for exactly as long as a tour exists.
  const activeTourId = useSelector((state) => state.productTour.activeTourId);

  if (!activeTourId) return null;

  return (
    <Suspense fallback={null}>
      <JoyrideTourRunner />
    </Suspense>
  );
};

export default JoyrideTourGate;
